import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "../header";

const OTPAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const aadhar = location.state?.aadhar || "";

  // OTP digits state (6 digits)
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(60);
  const [timerCompleted, setTimerCompleted] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");

  // Get OTP data from localStorage
  const otpData = JSON.parse(localStorage.getItem("userOTP"));
  const storedOtp = otpData?.otp ? String(otpData.otp) : "";

  // Timer logic
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setTimerCompleted(true);
    }
  }, [timer]);

  // Handle OTP input (6 digits)
  const handleInputChange = (index, value) => {
    // Allow only digits
    const cleaned = value.replace(/\D/g, "").slice(0, 1);
    const newDigits = [...otpDigits];
    newDigits[index] = cleaned;
    setOtpDigits(newDigits);

    // Auto-focus next input
    if (cleaned && index < 5) {
      const nextInput = document.getElementById(`otp-digit-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle backspace to move to previous input
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      const prevInput = document.getElementById(`otp-digit-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Combine digits into full OTP
  const getFullOtp = () => otpDigits.join("");

  // Verify OTP
  const handleVerifyOtp = () => {
    const enteredOtp = getFullOtp();

    if (enteredOtp.length !== 6) {
      toast.error("Please enter complete 6-digit OTP.");
      return;
    }

    if (!storedOtp) {
      toast.error("No OTP found. Please request a new one.");
      return;
    }

    if (enteredOtp === storedOtp) {
      toast.success("OTP verified successfully!");
      // Clear OTP data
      localStorage.removeItem("userOTP");
      // Navigate to success or dashboard
      navigate("/", { state: { abhaCreated: true } });
    } else {
      toast.error("Invalid OTP. Please try again.");
    }
  };

  // Resend OTP
  const handleResendOtp = () => {
    if (!aadhar) {
      toast.error("Aadhar number missing. Please go back.");
      navigate(-1);
      return;
    }

    // Generate new 6-digit OTP
    const newOtp = Math.floor(100000 + Math.random() * 900000);
    // Update localStorage
    localStorage.setItem(
      "userOTP",
      JSON.stringify({
        email: "",
        aadhar: aadhar,
        otp: newOtp,
        timestamp: Date.now(),
      })
    );

    // Reset timer
    setTimer(60);
    setTimerCompleted(false);
    // Clear OTP inputs
    setOtpDigits(["", "", "", "", "", ""]);
    // Focus first input
    document.getElementById("otp-digit-0")?.focus();

    toast.success(`New OTP sent. (Demo: ${newOtp})`);
  };

  // Cancel / Go back
  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <>
      <Header />
      <div className="mx-auto max-w-6xl border-2 border-gray-200 p-4 mt-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-800 mx-4 items-center">
          Confirm OTP
        </h2>

        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-800 mx-4 mb-2 items-center">
          OTP Sent to Aadhar mobile number ending with ******1400
        </h2>

        {/* OTP Input Group */}
        <div className="flex mb-2 justify-center">
          {otpDigits.map((digit, index) => (
            <div key={index} className="flex flex-col items-center">
              <input
                type="text"
                id={`otp-digit-${index}`}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                maxLength={1}
                className="w-12 h-12 border border-gray-600 rounded-md text-center text-xl text-black mx-1"
                inputMode="numeric"
                autoFocus={index === 0}
              />
            </div>
          ))}
        </div>

        {/* Timer and Resend */}
        <div className="flex items-center mx-4 mb-4">
          <p className="text-md font-medium text-gray-900 dark:text-gray-800">
            Didn't receive OTP ?
          </p>
          <button
            onClick={handleResendOtp}
            disabled={!timerCompleted}
            className={`text-md font-medium mx-4 mb-2 items-center ${
              timerCompleted
                ? "text-blue-600 hover:underline"
                : "text-gray-400 cursor-not-allowed"
            }`}
          >
            Resend OTP
          </button>
          {!timerCompleted && (
            <p className="text-md font-medium text-gray-900 dark:text-gray-800">
              00:{String(timer).padStart(2, "0")} remaining
            </p>
          )}
        </div>

        {/* Mobile Number Input (optional) */}
        <div className="flex-col mx-4">
          <p className="text-md font-medium text-gray-900 dark:text-gray-800">
            Mobile Number <span className="text-red-500">*</span>
          </p>
          <div className="max-w-xl mx-auto p-0">
            <div className="flex items-center mt-1">
              <span className="h-10 px-4 text-sm bg-gray-200 border border-r-0 border-gray-200 rounded-l-lg shadow-sm text-black flex items-center">
                +91
              </span>
              <input
                type="tel"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                className="w-full h-10 px-3 text-sm text-gray-700 border border-gray-200 focus:outline-none rounded-r shadow-sm"
                placeholder="Mobile Number"
                inputMode="numeric"
              />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end mt-3 mx-4">
          <button
            className="px-6 py-2 bg-gray-200 text-black text-sm font-medium rounded-md"
            onClick={handleCancel}
          >
            Cancel
          </button>

          <button
            className="px-6 py-2 ml-2 text-white text-sm font-medium rounded-md"
            style={{ backgroundColor: "rgb(214, 96, 37)" }}
            onClick={handleVerifyOtp}
          >
            Next
          </button>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default OTPAuth;